'use client';

import { useEffect, useMemo, useState } from 'react';
import { Check, Minus, Plus, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hasProductPrice } from '@sweetspot/shared';
import { useStorefrontCart } from '@/lib/storefront-cart-context';

interface AddToCartProduct {
  id: string;
  title: string;
  price: number | string;
  /** Availability uses the catalog price, before buyer discounts. */
  basePrice?: number | string;
  unit?: string | null;
  minQty?: number | null;
  imageUrl?: string | null;
}

export function StorefrontAddToCartButton({
  slug,
  vendorName,
  vendorAvatarUrl,
  product,
  compact = false,
}: {
  slug: string;
  vendorName: string;
  vendorAvatarUrl?: string | null;
  product: AddToCartProduct;
  compact?: boolean;
}) {
  const { items, addItem } = useStorefrontCart();
  const priceAvailable = hasProductPrice(product.basePrice ?? product.price);
  const [justAdded, setJustAdded] = useState(false);
  const minQty = useMemo(() => Math.max(1, Number(product.minQty ?? 1)), [product.minQty]);
  const [selectedQtyInput, setSelectedQtyInput] = useState(String(minQty));

  useEffect(() => {
    setSelectedQtyInput(String(minQty));
  }, [product.id, minQty]);

  const inCartQty = useMemo(() => {
    const existing = items.find((item) => item.slug === slug && item.productId === product.id);
    return existing?.quantity ?? 0;
  }, [items, slug, product.id]);

  const handleClick = () => {
    if (!priceAvailable) return;
    const parsedQty = Number.parseInt(selectedQtyInput, 10);
    const quantity = Number.isFinite(parsedQty) ? Math.max(minQty, parsedQty) : minQty;
    if (selectedQtyInput !== String(quantity)) {
      setSelectedQtyInput(String(quantity));
    }

    addItem({
      slug,
      vendorName,
      vendorAvatarUrl: vendorAvatarUrl ?? undefined,
      productId: product.id,
      title: product.title,
      price: Number(product.price),
      unit: product.unit ?? 'each',
      minQty,
      imageUrl: product.imageUrl ?? undefined,
      quantity: compact ? quantity : minQty,
    });
    setJustAdded(true);
    window.setTimeout(() => setJustAdded(false), 1100);
  };

  if (!priceAvailable) {
    return <Button type="button" disabled className={compact ? 'w-full' : undefined}>Coming soon</Button>;
  }

  if (compact) {
    return (
      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
        <div className="flex h-8 min-w-0 items-center rounded-md border border-input bg-white">
          <button
            type="button"
            className="inline-flex h-full w-6 shrink-0 items-center justify-center text-muted-foreground hover:bg-accent"
            onClick={() =>
              setSelectedQtyInput((current) => {
                const parsed = Number.parseInt(current, 10);
                const base = Number.isFinite(parsed) ? Math.max(minQty, parsed) : minQty;
                return String(Math.max(minQty, base - minQty));
              })
            }
            aria-label={`Decrease quantity for ${product.title}`}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <input
            type="number"
            min={minQty}
            step={minQty}
            inputMode="numeric"
            value={selectedQtyInput}
            onChange={(e) => setSelectedQtyInput(e.target.value)}
            onBlur={() =>
              setSelectedQtyInput((current) => {
                const parsed = Number.parseInt(current, 10);
                if (!Number.isFinite(parsed)) return String(minQty);
                return String(Math.max(minQty, parsed));
              })
            }
            aria-label={`Quantity for ${product.title}`}
            className="h-full w-10 min-w-0 border-0 bg-transparent px-0 text-center text-xs font-semibold tabular-nums outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <button
            type="button"
            className="inline-flex h-full w-6 shrink-0 items-center justify-center text-muted-foreground hover:bg-accent"
            onClick={() =>
              setSelectedQtyInput((current) => {
                const parsed = Number.parseInt(current, 10);
                const base = Number.isFinite(parsed) ? Math.max(minQty, parsed) : minQty;
                return String(base + minQty);
              })
            }
            aria-label={`Increase quantity for ${product.title}`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <Button
          type="button"
          onClick={handleClick}
          variant={justAdded ? 'secondary' : 'default'}
          size="icon"
          className="h-8 w-8 shrink-0 p-0"
          aria-label={`Add ${selectedQtyInput || minQty} of ${product.title} to cart`}
        >
          {justAdded ? <Check className="h-3.5 w-3.5" /> : <ShoppingCart className="h-3.5 w-3.5" />}
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      onClick={handleClick}
      variant={justAdded ? 'secondary' : 'default'}
      size="default"
      className="gap-2"
    >
      {justAdded ? <Check className="h-3.5 w-3.5" /> : <ShoppingCart className="h-3.5 w-3.5" />}
      {justAdded ? 'Added' : 'Add to cart'}
      {inCartQty > 0 && <span className="text-[11px] opacity-90">({inCartQty})</span>}
    </Button>
  );
}
